import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

/**
 * EventCard - Tarjeta de Evento/Pozo
 */
const EventCard = ({ event, onClick, onDelete, onUploadPoster, className = '' }) => {
    const navigate = useNavigate();

    const {
        id,
        title,
        club,
        date,
        startTime,
        levelMin,
        levelMax,
        pricePerPlayer,
        currentPairs,
        maxPairs,
        status,
        posterUrl
    } = event;

    // Calcular porcentaje de ocupación (currentPairs son JUGADORES, maxPairs son PAREJAS)
    const totalSpots = maxPairs * 2;
    const occupancyPercentage = (currentPairs / totalSpots) * 100;

    // Determinar badge de estado
    const getStatusBadge = () => {
        if (status === 'full' || currentPairs >= totalSpots) {
            return { text: 'Completo', color: 'bg-error text-white', icon: '🔴' };
        } else if (occupancyPercentage >= 70) {
            return { text: 'Últimas plazas', color: 'bg-warning text-white', icon: '🟡' };
        } else {
            return { text: 'Plazas disponibles', color: 'bg-success text-white', icon: '🟢' };
        }
    };

    const badge = getStatusBadge();
    
    // Safely format date
    let formattedDate = 'Fecha por confirmar';
    try {
        if (date) {
            formattedDate = format(new Date(date), "EEE d MMM", { locale: es });
        }
    } catch (e) {
        console.warn('Invalid date for event', id, date);
    }

    const handleClick = () => {
        if (onClick) {
            onClick(event);
        } else {
            navigate(`/events/${id}`);
        }
    };

    // Safe club access
    const clubName = club?.name || 'Club no especificado';
    const clubCity = club?.city || '';
    const clubImage = club?.photoUrl || '/padel_lines.png';

    return (
        <div className={`bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden relative group ${className}`}>

            {/* Header con imagen - Clickable */}
            <div
                className="relative h-44 bg-padel-branded overflow-hidden cursor-pointer border-b border-gray-100"
                onClick={handleClick}
            >
                <img
                    src={posterUrl || clubImage}
                    alt={clubName}
                    className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out ${posterUrl ? 'opacity-100' : 'opacity-90'}`}
                    loading="lazy"
                />

                {/* Promo Badge if poster exists */}
                {posterUrl && (
                    <div className="absolute bottom-3 left-3 z-10">
                        <span className="bg-accent-500 text-white text-[10px] font-black px-2 py-1 rounded shadow-lg uppercase tracking-widest animate-pulse">
                            Promo
                        </span>
                    </div>
                )}

                {/* Badge - Visualmente posicionado para dejar espacio a la papelera si existe */}
                <div className="absolute top-3 right-3 flex gap-2 z-10 pointer-events-none">
                    <span className={`
                        ${badge.color} text-xs font-semibold px-3 py-1.5 rounded-full
                        shadow-lg flex items-center gap-1.5 
                        ${onDelete ? 'mr-12' : ''} 
                     `}>
                        <span>{badge.icon}</span>
                        <span>{badge.text}</span>
                    </span>
                </div>

                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>

            {/* Content */}
            <div className="p-4 space-y-3">
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary-600 transition-colors line-clamp-1 cursor-pointer" onClick={handleClick} title={title}>
                    {title}
                </h3>

                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="text-base flex-shrink-0">📍</span>
                    <span className="line-clamp-1 break-all">{clubName} - {clubCity}</span>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="text-base flex-shrink-0">📅</span>
                    <span className="truncate">{formattedDate} - {startTime}</span>
                </div>

                {/* Distance Badge if available */}
                {event.distance !== undefined && event.distance !== null && (
                    <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-primary-600 bg-primary-50 px-2 py-1 rounded-lg w-fit">
                        <span>🚗 A {event.distance.toFixed(1)} km de ti</span>
                    </div>
                )}

                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <div className="flex items-center gap-2 overflow-hidden">
                        <span className="text-base flex-shrink-0">🎯</span>
                        <span className="text-sm font-semibold text-gray-700 truncate">Nivel {levelMin} - {levelMax}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xl font-bold bg-padel-branded bg-clip-text text-transparent filter brightness-90">{pricePerPlayer}€</span>
                        <span className="text-xs text-gray-500">/jugador</span>
                    </div>
                </div>

                <div className="space-y-1">
                    <div className="flex justify-between text-xs text-gray-500">
                        <span>{currentPairs} inscritos</span>
                        <span>{totalSpots} plazas</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all duration-500 ${occupancyPercentage < 70 ? 'bg-success' : occupancyPercentage < 100 ? 'bg-warning' : 'bg-error'}`}
                            style={{ width: `${Math.min(occupancyPercentage, 100)}%` }}
                        />
                    </div>
                </div>

                <button
                    className="w-full bg-primary-600 text-white font-black py-4 px-4 rounded-xl shadow-xl hover:bg-primary-700 transition-all transform active:scale-95 flex items-center justify-center gap-3 mt-3 border-2 border-white/10 uppercase tracking-widest text-xs"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleClick();
                    }}
                >
                    <span>{status === 'full' ? 'Ver lista de espera' : 'Ver detalles'}</span>
                    <span className="text-xl group-hover:translate-x-1 transition-transform">➡️</span>
                </button>
            </div>

            {/* DELETE BUTTON - Completely separate and on top - SOLID RED FOR VISIBILITY */}
            {onDelete && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDelete(id);
                    }}
                    className="absolute top-3 right-3 bg-white text-error p-2 rounded-full hover:bg-error hover:text-white shadow-lg transition-transform active:scale-95 flex items-center justify-center cursor-pointer z-[100]"
                    style={{ pointerEvents: 'auto' }}
                    title="Eliminar pozo"
                >
                    <svg className="w-5 h-5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                </button>
            )}

            {/* UPLOAD POSTER BUTTON - Only if handler is provided */}
            {onUploadPoster && (
                <div className="absolute top-3 left-3 z-[100]">
                    <label className="bg-white/90 backdrop-blur-sm text-primary-600 p-2 rounded-full hover:bg-primary-600 hover:text-white shadow-lg transition-all active:scale-95 flex items-center justify-center cursor-pointer border border-primary-100">
                        <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    const reader = new FileReader();
                                    reader.onloadend = () => {
                                        onUploadPoster(id, reader.result);
                                    };
                                    reader.readAsDataURL(file);
                                }
                            }}
                        />
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                    </label>
                </div>
            )}
        </div>
    );
};

// Compact variant
EventCard.Compact = ({ event, onClick }) => {
    const navigate = useNavigate();
    const { id, title, club, date, startTime, levelMin, levelMax, pricePerPlayer, currentPairs, maxPairs } = event;
    const handleClick = () => onClick ? onClick(event) : navigate(`/events/${id}`);
    
    // Safely format date
    let formattedDate = 'Fecha?';
    try {
        if (date) formattedDate = format(new Date(date), 'dd/MM');
    } catch(e) {}

    const clubName = club?.name || 'Club?';

    return (
        <div className="bg-white rounded-xl shadow-card hover:shadow-card-hover p-4 cursor-pointer transition-all duration-200" onClick={handleClick}>
            <div className="flex items-center justify-between">
                <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-1">{title}</h4>
                    <p className="text-sm text-gray-600">{clubName}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span>📅 {formattedDate} - {startTime}</span>
                        <span>🎯 {levelMin}-{levelMax}</span>
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-xl font-bold text-primary-600 mb-1">{pricePerPlayer}€</div>
                    <div className="text-xs text-gray-500">{currentPairs}/{maxPairs * 2} plazas</div>
                </div>
            </div>
        </div>
    );
};

// Featured variant
EventCard.Featured = (props) => {
    const { event, onClick, onDelete } = props;
    return (
        <div className="relative">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10 pointer-events-none">
                <span className="bg-accent-500 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg">🔥 Evento Destacado</span>
            </div>
            <EventCard event={event} onClick={onClick} onDelete={onDelete} className="border-2 border-accent-500 shadow-glow-accent" />
        </div>
    );
};

EventCard.Skeleton = () => (
    <div className="bg-white rounded-2xl shadow-card overflow-hidden animate-pulse">
        <div className="h-40 bg-gray-200" />
        <div className="p-4 space-y-3">
            <div className="h-6 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="h-8 bg-gray-200 rounded mt-4" />
        </div>
    </div>
);

export default EventCard;
